import { ChartValueType, ConfigKey } from '../constants';
import {
  Gray5,
  Gray6,
  Gray7,
  Gray12,
  Green3,
  GreenPrimary,
  Red3,
  RedPrimary,
  defaultChartColors,
} from '../styles';

/**
 * Get chart colors from organization config or defaults
 */
const getChartColors = (organizationConfigs) => {
  if (organizationConfigs?.data) {
    return {
      [ConfigKey.PRIMARY_CHART_COLOR]:
        organizationConfigs.data[ConfigKey.PRIMARY_CHART_COLOR] ||
        defaultChartColors[ConfigKey.PRIMARY_CHART_COLOR],
      [ConfigKey.SECONDARY_CHART_COLOR]:
        organizationConfigs.data[ConfigKey.SECONDARY_CHART_COLOR] ||
        defaultChartColors[ConfigKey.SECONDARY_CHART_COLOR],
    };
  }
  return { ...defaultChartColors };
};

/**
 * Common tooltip formatter base styling
 */
const tooltipBaseConfig = {
  backgroundColor: '#F6F6F6',
  borderColor: '#999999',
  borderWidth: 1,
  borderRadius: 3,
  padding: 8,
  textStyle: {
    fontFamily: 'Roboto',
    fontSize: 12,
    color: '#333333',
  },
  extraCssText: 'box-shadow: 0 2px 4px rgba(0,0,0,0.15);',
};

/**
 * Generates horizontal bar chart config for theme distribution
 */
export const getThemesBarChart = ({
  themes,
  questionId,
  surveyId,
  filters = undefined,
  chartValueType,
  highlightThemeIds = [],
  clickable = true,
  organizationConfigs,
}) => {
  const chartColors = getChartColors(organizationConfigs);
  const chartData = [];
  const chartLinks = [];

  themes.forEach((t) => {
    const { name, id, percentage, response_count: responseCount } = t.data;
    const value = chartValueType === ChartValueType.PERCENTAGE ? percentage : responseCount;

    chartData.push({
      name,
      value,
      themeId: id,
      percentage,
      responseCount,
      isGroup: true,
      itemStyle: {
        color:
          highlightThemeIds.length && !highlightThemeIds.includes(id)
            ? Gray7
            : chartColors[ConfigKey.PRIMARY_CHART_COLOR],
      },
    });

    if (clickable) {
      chartLinks.push({
        themeId: id,
        link: `${window.location.origin}/surveys/${surveyId}/questions/${questionId}/themes/${id}/responses${filters ? `?${filters}` : ''}`,
      });
    }

    t.children.forEach((c) => {
      const childValue =
        chartValueType === ChartValueType.PERCENTAGE ? c.data.percentage : c.data.response_count;
      chartData.push({
        name: c.data.name,
        value: childValue,
        themeId: c.data.id,
        percentage: c.data.percentage,
        responseCount: c.data.response_count,
        isGroup: false,
        itemStyle: {
          color:
            highlightThemeIds.length && !highlightThemeIds.includes(c.data.id)
              ? Gray5
              : chartColors[ConfigKey.SECONDARY_CHART_COLOR],
        },
      });

      if (clickable) {
        chartLinks.push({
          themeId: c.data.id,
          link: `${window.location.origin}/surveys/${surveyId}/questions/${questionId}/themes/${c.data.id}/responses${filters ? `?${filters}` : ''}`,
        });
      }
    });
  });

  // Calculate height based on number of items - increased spacing for multi-line labels
  const chartHeight = Math.max(35 * chartData.length, 500);

  return {
    height: chartHeight + 40,
    links: chartLinks,
    clickable,
    option: {
      tooltip: {
        ...tooltipBaseConfig,
        trigger: 'item',
        formatter: (params) => {
          const { name, data } = params;
          return `${data.percentage}% (${data.responseCount}) of responses contain the theme ${name}`;
        },
      },
      grid: {
        left: 240,
        right: '8%',
        top: 25,
        bottom: 8,
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        axisLine: { show: true, lineStyle: { color: '#E6E6E6', opacity: 0.25 } },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: 'Roboto',
          fontSize: 12,
          color: '#666666',
          formatter: chartValueType === ChartValueType.PERCENTAGE ? '{value}%' : '{value}',
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'category',
        data: chartData.map((d) => d.name).reverse(),
        inverse: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: 'Roboto',
          fontSize: 12,
          color: Gray12,
          width: 200,
          overflow: 'break',
          lineHeight: 16,
        },
      },
      series: [
        {
          type: 'bar',
          data: [...chartData].reverse(),
          barMaxWidth: 30,
          emphasis: {
            itemStyle: {
              opacity: 0.95,
            },
          },
          label: {
            show: true,
            position: 'right',
            fontFamily: 'Roboto',
            fontSize: 12,
            fontWeight: 'bold',
            color: '#000000',
            formatter: (params) =>
              chartValueType === ChartValueType.PERCENTAGE ? `${params.value}%` : `${params.value}`,
          },
        },
      ],
    },
  };
};

/**
 * Generates column chart config for closed-ended (multiple choice) questions
 */
export const getChartForClosedEndedQuestions = (aggregatedResponses, chartTitle) => {
  const chartData = aggregatedResponses.map((response) => ({
    name: response.response_text,
    value: response.percentage,
    count: response.respondent_count,
  }));

  return {
    height: 400,
    option: {
      title: {
        text: chartTitle,
        left: 'center',
        textStyle: {
          fontFamily: 'Roboto',
          fontSize: 18,
          fontWeight: 'normal',
          color: '#333333',
        },
      },
      tooltip: {
        ...tooltipBaseConfig,
        trigger: 'item',
        formatter: (params) => {
          const { name, data } = params;
          const personWord = data.count === 1 ? 'person' : 'people';
          return `${data.count} ${personWord} (${data.value}% of all respondents to this question) chose ${name}`;
        },
      },
      grid: {
        left: '10%',
        right: '10%',
        top: 60,
        bottom: 60,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: chartData.map((d) => d.name),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: 'Roboto',
          fontSize: 12,
          color: '#666666',
          interval: 0,
          rotate: chartData.length > 5 ? 30 : 0,
          width: 180,
          overflow: 'break',
          lineHeight: 16,
          formatter(value) {
            // Split long text into two lines if needed
            if (typeof value === 'string' && value.length > 40) {
              const midpoint = Math.floor(value.length / 2);
              let splitIdx = value.indexOf(' ', midpoint);
              if (splitIdx === -1 || splitIdx > midpoint + 15)
                splitIdx = value.lastIndexOf(' ', midpoint);
              if (splitIdx > 10 && splitIdx < value.length - 10) {
                return `${value.slice(0, splitIdx)}\n${value.slice(splitIdx + 1)}`;
              }
            }
            return value;
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: 'Roboto',
          fontSize: 12,
          color: '#666666',
          formatter: '{value}%',
        },
        splitLine: {
          lineStyle: { color: '#E6E6E6' },
        },
      },
      series: [
        {
          type: 'bar',
          data: chartData,
          barMaxWidth: 50,
          itemStyle: {
            color: '#FAAD14',
          },
          emphasis: {
            itemStyle: {
              color: '#FFD666',
            },
          },
          label: {
            show: true,
            position: 'top',
            fontFamily: 'Roboto',
            fontSize: 12,
            fontWeight: 'bold',
            color: '#000000',
            lineHeight: 16,
            overflow: 'break',
            formatter: '{c}%',
          },
        },
      ],
    },
  };
};

/**
 * Generates stacked horizontal bar chart for sentiment distribution across themes
 */
export const getSentimentsBarChart = (themes) => {
  const categories = themes.flatMap((t) => {
    const childLabels = t.children.map((c) => c.data.name);
    return [t.data.name, ...childLabels];
  });

  const calcPercent = (count, total) => (total ? Math.round((count * 100) / total) : 0);

  // Build percentage-based sentiment data so each row sums to 100%
  const positiveData = [];
  const negativeData = [];
  const neutralData = [];

  themes.forEach((t) => {
    const pTotal = t.data.positive_count + t.data.negative_count + t.data.neutral_count;
    positiveData.push({
      value: calcPercent(t.data.positive_count, pTotal),
      itemStyle: { color: GreenPrimary },
    });
    negativeData.push({
      value: calcPercent(t.data.negative_count, pTotal),
      itemStyle: { color: RedPrimary },
    });
    neutralData.push({
      value: calcPercent(t.data.neutral_count, pTotal),
      itemStyle: { color: Gray6 },
    });

    t.children.forEach((c) => {
      const cTotal = c.data.positive_count + c.data.negative_count + c.data.neutral_count;
      positiveData.push({
        value: calcPercent(c.data.positive_count, cTotal),
        itemStyle: { color: Green3 },
      });
      negativeData.push({
        value: calcPercent(c.data.negative_count, cTotal),
        itemStyle: { color: Red3 },
      });
      neutralData.push({
        value: calcPercent(c.data.neutral_count, cTotal),
        itemStyle: { color: Gray5 },
      });
    });
  });

  // Calculate height based on categories - increased spacing for multi-line labels
  const chartHeight = 160 + Math.max(28 * categories.length, 500);

  return {
    height: chartHeight,
    option: {
      title: {
        text: 'Sentiment Distribution',
        subtext: 'Muted colors represent sub-themes',
        left: 'center',
        textStyle: {
          fontFamily: 'Roboto',
          fontSize: 18,
          fontWeight: 'normal',
          color: '#333333',
        },
        subtextStyle: {
          fontFamily: 'Roboto',
          fontSize: 12,
          color: '#666666',
        },
      },
      tooltip: {
        ...tooltipBaseConfig,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      legend: {
        data: ['Positive', 'Negative', 'Neutral'],
        bottom: 0,
        textStyle: {
          fontFamily: 'Roboto',
          fontSize: 15,
          color: '#7C7C7C',
        },
      },
      grid: {
        left: '30%',
        right: '5%',
        top: 80,
        bottom: 50,
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        max: 100,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: 'Roboto',
          fontSize: 12,
          color: '#666666',
          formatter: '{value}%',
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'category',
        data: categories.reverse(),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontFamily: 'Roboto',
          fontSize: 12,
          color: Gray12,
          width: 200,
          overflow: 'break',
          lineHeight: 16,
        },
      },
      series: [
        {
          name: 'Positive',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: GreenPrimary },
          data: [...positiveData].reverse(),
          barMaxWidth: 20,
          label: {
            show: true,
            position: 'inside',
            fontFamily: 'Roboto',
            fontSize: 11,
            color: '#FFFFFF',
            formatter: (params) => (params.value > 0 ? `${params.value}%` : ''),
          },
        },
        {
          name: 'Negative',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: RedPrimary },
          data: [...negativeData].reverse(),
          barMaxWidth: 20,
          label: {
            show: true,
            position: 'inside',
            fontFamily: 'Roboto',
            fontSize: 11,
            color: '#FFFFFF',
            formatter: (params) => (params.value > 0 ? `${params.value}%` : ''),
          },
        },
        {
          name: 'Neutral',
          type: 'bar',
          stack: 'total',
          itemStyle: { color: Gray6 },
          data: [...neutralData].reverse(),
          barMaxWidth: 20,
          label: {
            show: true,
            position: 'inside',
            fontFamily: 'Roboto',
            fontSize: 11,
            color: '#FFFFFF',
            formatter: (params) => (params.value > 0 ? `${params.value}%` : ''),
          },
        },
      ],
    },
  };
};

/**
 * Generates a simple stacked horizontal bar chart for theme sentiment (single bar)
 */
export const getThemeSentimentBarChart = ({ positiveCount, negativeCount, neutralCount }) => {
  const totalCount = positiveCount + negativeCount + neutralCount;

  const positivePercentage = totalCount ? Math.round((positiveCount * 100) / totalCount) : 0;
  const negativePercentage = totalCount ? Math.round((negativeCount * 100) / totalCount) : 0;
  const neutralPercentage = totalCount ? Math.round((neutralCount * 100) / totalCount) : 0;

  return {
    height: 75,
    option: {
      tooltip: {
        ...tooltipBaseConfig,
        trigger: 'item',
      },
      legend: {
        data: [
          `${positivePercentage}% positive`,
          `${negativePercentage}% negative`,
          `${neutralPercentage}% neutral`,
        ],
        bottom: 20,
        textStyle: {
          fontFamily: 'Roboto',
          fontSize: 12,
          color: '#7C7C7C',
        },
        itemWidth: 14,
        itemHeight: 14,
      },
      grid: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 60,
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        max: 100,
        show: false,
      },
      yAxis: {
        type: 'category',
        data: [''],
        show: false,
      },
      series: [
        {
          name: `${positivePercentage}% positive`,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: GreenPrimary },
          data: [
            {
              value: positivePercentage,
              itemStyle: { color: GreenPrimary },
            },
          ],
          barMaxWidth: 24,
          tooltip: {
            formatter: () => `<b>${positiveCount}</b> responses are <b>Positive</b>`,
          },
        },
        {
          name: `${negativePercentage}% negative`,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: RedPrimary },
          data: [
            {
              value: negativePercentage,
              itemStyle: { color: RedPrimary },
            },
          ],
          barMaxWidth: 24,
          tooltip: {
            formatter: () => `<b>${negativeCount}</b> responses are <b>Negative</b>`,
          },
        },
        {
          name: `${neutralPercentage}% neutral`,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: Gray6 },
          data: [
            {
              value: neutralPercentage,
              itemStyle: { color: Gray6 },
            },
          ],
          barMaxWidth: 24,
          tooltip: {
            formatter: () => `<b>${neutralCount}</b> responses are <b>Neutral</b>`,
          },
        },
      ],
    },
  };
};

/**
 * Generates doughnut (donut) chart for overall sentiment distribution
 */
export const getSentimentsDonutChart = ({ caption, sentiments }) => {
  return {
    height: 350,
    option: {
      title: {
        text: caption,
        left: 'center',
        top: 0,
        textStyle: {
          fontFamily: 'Roboto',
          fontSize: 18,
          fontWeight: 'normal',
          color: '#333333',
        },
      },
      tooltip: {
        ...tooltipBaseConfig,
        trigger: 'item',
        formatter: (params) => `<b>${params.value}</b> responses are <b>${params.name}</b>`,
      },
      legend: {
        orient: 'horizontal',
        bottom: 10,
        textStyle: {
          fontFamily: 'Roboto',
          fontSize: 15,
          color: '#7C7C7C',
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '60%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderWidth: 0,
          },
          label: {
            show: true,
            fontFamily: 'Roboto',
            fontSize: 12,
            formatter: '{d}%',
          },
          emphasis: {
            label: {
              show: true,
              fontWeight: 'bold',
            },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          labelLine: {
            show: true,
          },
          data: [
            {
              name: 'Positive',
              value: sentiments.positive_count,
              itemStyle: { color: GreenPrimary },
            },
            {
              name: 'Negative',
              value: sentiments.negative_count,
              itemStyle: { color: RedPrimary },
            },
            {
              name: 'Neutral',
              value: sentiments.neutral_count,
              itemStyle: { color: Gray6 },
            },
          ],
        },
      ],
    },
  };
};

/**
 * Generates a multi-grid chart for displaying multiple segments side by side
 * Each segment gets its own grid, but they share the same y-axis categories
 */
export const getMultiGridThemesChart = ({
  segments, // Array of { id, label, themes, responseCount, filters }
  questionId,
  surveyId,
  chartValueType,
  highlightThemeIds = [],
  organizationConfigs,
  maxSegmentsPerRow = 4, // Used to maintain consistent sizing across rows
  containerWidth = 1200, // Container width in px, used for responsive label sizing
}) => {
  const chartColors = getChartColors(organizationConfigs);
  const segmentCount = segments.length;

  if (segmentCount === 0) {
    return { height: 0, option: {}, links: [] };
  }

  // Build category labels from first segment (all segments have same themes structure)
  const categories = [];
  const allLinks = [];

  segments[0].themes.forEach((t) => {
    categories.push(t.data.name);
    t.children.forEach((c) => categories.push(c.data.name));
  });

  const categoryCount = categories.length;
  const chartHeight = Math.max(35 * categoryCount, 500);

  // Calculate max value across all segments for consistent x-axis
  let maxValue = 0;
  segments.forEach((segment) => {
    segment.themes.forEach((t) => {
      const value =
        chartValueType === ChartValueType.PERCENTAGE ? t.data.percentage : t.data.response_count;
      if (value > maxValue) maxValue = value;
      t.children.forEach((c) => {
        const childValue =
          chartValueType === ChartValueType.PERCENTAGE ? c.data.percentage : c.data.response_count;
        if (childValue > maxValue) maxValue = childValue;
      });
    });
  });

  // Round up max value to next multiple of 5
  let roundedMax = maxValue;
  if (maxValue % 5 === 0) {
    if (chartValueType !== ChartValueType.PERCENTAGE || maxValue !== 100) {
      roundedMax += 5;
    }
  } else {
    roundedMax = Math.ceil(maxValue / 5) * 5;
  }

  // Calculate grid positions based on segment count (all percentage-based for responsive scaling)
  const grids = [];
  const xAxes = [];
  const yAxes = [];
  const series = [];
  const titles = [];

  // Adaptive label area percentage — wider when fewer columns to give labels more room
  const getLabelPercent = (colCount) => {
    if (colCount <= 1) return 22;
    if (colCount <= 2) return 20;
    return 18;
  };

  // Calculate available width for bar areas (as percentage)
  // Use maxSegmentsPerRow to ensure consistent sizing across rows (when there are multiple rows)
  const getGridConfig = (idx, total, maxPerRow) => {
    // Use maxPerRow if provided and greater than total (for consistent multi-row sizing)
    // Otherwise use total (for dynamic single-row sizing)
    const referenceTotal = maxPerRow > total ? maxPerRow : total;
    const labelPercent = getLabelPercent(referenceTotal);
    const rightMarginPercent = 3;

    // For single segment with no maxPerRow constraint, use full available width
    if (referenceTotal === 1) {
      return { left: `${labelPercent}%`, right: `${rightMarginPercent}%` };
    }

    // For multiple segments, distribute space evenly based on referenceTotal
    const availablePercent = 100 - labelPercent - rightMarginPercent;
    const segmentWidth = availablePercent / referenceTotal;
    const gapPercent = referenceTotal >= 3 ? 2 : 1; // wider gap with more columns to avoid label overlap

    return {
      left: `${labelPercent + segmentWidth * idx + gapPercent}%`,
      width: `${segmentWidth - gapPercent * 2}%`,
    };
  };

  // Scale fonts based on how many segments are displayed and available width
  const effectiveColumns = Math.min(segmentCount, maxSegmentsPerRow);
  const titleFontSize = effectiveColumns >= 4 ? 11 : 13;
  const xAxisFontSize = effectiveColumns >= 4 ? 10 : 11;
  const labelFontSize = effectiveColumns >= 3 ? 11 : 12;
  // Compute y-axis label width from container width and label percent
  const labelPercent = getLabelPercent(effectiveColumns);
  const yAxisLabelWidth = Math.max(100, Math.floor((containerWidth * labelPercent) / 100) - 20);

  segments.forEach((segment, idx) => {
    const gridConfig = getGridConfig(idx, segmentCount, maxSegmentsPerRow);

    // Compute title center position within the grid
    const titleLeft = gridConfig.width
      ? `${parseFloat(gridConfig.left) + parseFloat(gridConfig.width) / 2}%`
      : `${(parseFloat(gridConfig.left) + (100 - parseFloat(gridConfig.right || 3))) / 2}%`;

    // Compute max title width from grid width to prevent overlap
    const titleMaxWidth = gridConfig.width
      ? Math.floor((containerWidth * parseFloat(gridConfig.width)) / 100)
      : containerWidth - 100;

    // Add segment title
    titles.push({
      text: `${segment.label} (${segment.responseCount})`,
      left: titleLeft,
      top: 5,
      textAlign: 'center',
      textStyle: {
        fontFamily: 'Roboto',
        fontSize: titleFontSize,
        fontWeight: 'normal',
        color: '#333333',
        width: titleMaxWidth,
        overflow: 'break',
        lineHeight: titleFontSize + 4,
      },
    });

    // Grid configuration — all percentage-based
    grids.push({
      id: `grid-${idx}`,
      left: gridConfig.left,
      right: gridConfig.right || '1%',
      width: gridConfig.width,
      top: 35,
      bottom: 30,
      containLabel: false,
    });

    // X-axis for this grid
    xAxes.push({
      type: 'value',
      gridIndex: idx,
      max: roundedMax,
      axisLine: { show: true, lineStyle: { color: '#E6E6E6', opacity: 0.25 } },
      axisTick: { show: false },
      axisLabel: {
        fontFamily: 'Roboto',
        fontSize: xAxisFontSize,
        color: '#666666',
        formatter: chartValueType === ChartValueType.PERCENTAGE ? '{value}%' : '{value}',
        hideOverlap: true,
        overflow: 'truncate',
      },
      splitLine: { show: false },
    });

    // Y-axis for this grid (only show labels on first)
    const isFirst = idx === 0;
    yAxes.push({
      type: 'category',
      gridIndex: idx,
      data: [...categories].reverse(),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: isFirst
        ? {
            fontFamily: 'Roboto',
            fontSize: labelFontSize,
            color: Gray12,
            width: yAxisLabelWidth,
            overflow: 'break',
            lineHeight: 16,
          }
        : { show: false },
    });

    // Build series data for this segment
    const segmentData = [];
    const segmentLinks = [];

    segment.themes.forEach((t) => {
      const { name, id, percentage, response_count: responseCount } = t.data;
      const value = chartValueType === ChartValueType.PERCENTAGE ? percentage : responseCount;

      segmentData.push({
        name,
        value,
        themeId: id,
        segmentIdx: idx,
        percentage,
        responseCount,
        isGroup: true,
        itemStyle: {
          color:
            highlightThemeIds.length && !highlightThemeIds.includes(id)
              ? Gray7
              : chartColors[ConfigKey.PRIMARY_CHART_COLOR],
        },
      });

      segmentLinks.push({
        themeId: id,
        segmentIdx: idx,
        link: `${window.location.origin}/surveys/${surveyId}/questions/${questionId}/themes/${id}/responses${segment.filters ? `?${segment.filters}` : ''}`,
      });

      t.children.forEach((c) => {
        const childValue =
          chartValueType === ChartValueType.PERCENTAGE ? c.data.percentage : c.data.response_count;

        segmentData.push({
          name: c.data.name,
          value: childValue,
          themeId: c.data.id,
          segmentIdx: idx,
          percentage: c.data.percentage,
          responseCount: c.data.response_count,
          isGroup: false,
          itemStyle: {
            color:
              highlightThemeIds.length && !highlightThemeIds.includes(c.data.id)
                ? Gray5
                : chartColors[ConfigKey.SECONDARY_CHART_COLOR],
          },
        });

        segmentLinks.push({
          themeId: c.data.id,
          segmentIdx: idx,
          link: `${window.location.origin}/surveys/${surveyId}/questions/${questionId}/themes/${c.data.id}/responses${segment.filters ? `?${segment.filters}` : ''}`,
        });
      });
    });

    allLinks.push(...segmentLinks);

    series.push({
      type: 'bar',
      xAxisIndex: idx,
      yAxisIndex: idx,
      data: [...segmentData].reverse(),
      barMaxWidth: 30,
      emphasis: {
        itemStyle: {
          opacity: 0.95,
        },
      },
      label: {
        show: true,
        position: 'right',
        fontFamily: 'Roboto',
        fontSize: xAxisFontSize,
        fontWeight: 'bold',
        color: '#000000',
        overflow: 'truncate',
        formatter: (params) =>
          chartValueType === ChartValueType.PERCENTAGE ? `${params.value}%` : `${params.value}`,
      },
    });
  });

  return {
    height: chartHeight + 65, // Extra space for titles (35) and bottom axis (30)
    links: allLinks,
    clickable: true,
    option: {
      title: titles,
      tooltip: {
        ...tooltipBaseConfig,
        trigger: 'item',
        formatter: (params) => {
          const { name, data } = params;
          return `${data.percentage}% (${data.responseCount}) of responses contain the theme ${name}`;
        },
      },
      grid: grids,
      xAxis: xAxes,
      yAxis: yAxes,
      series,
    },
  };
};
